import { BillingSubscriptionStatus } from '@sandtech-hub-monorepo/ehub-client';
import { formatDate } from '@sandtech-hub-monorepo/ui-kit-site';

import { StripeSubscriptionsQueryRow } from '../../../data/useStripeSubscriptions';

/**
 * How a status reads to the learner, rather than which enum value it is.
 *
 * The eight Stripe statuses do not sort into "working" and "not working", and
 * the two that surprise people both matter here. PAST_DUE still grants access
 * while Stripe retries, so showing it as a failure would tell someone their
 * access is gone while it is not. UNPAID and PAUSED have stopped access but
 * still hold the plan slot, so they are not the same as cancelled either.
 */
export type StatusTone = 'good' | 'attention' | 'ended';

interface SubscriptionStatusCopy {
  headline: string;
  detail: string;
  tone: StatusTone;
}

export const describeSubscription = ({
  status,
  cancelAtPeriodEnd,
  currentPeriodEnd,
}: StripeSubscriptionsQueryRow): SubscriptionStatusCopy => {
  const endDate = formatDate(currentPeriodEnd);

  // Checked before the status, because a cancelled-at-period-end subscription
  // is still ACTIVE and would otherwise be reported as renewing on the very
  // date it ends.
  if (
    cancelAtPeriodEnd &&
    (status === BillingSubscriptionStatus.Active ||
      status === BillingSubscriptionStatus.Trialing)
  ) {
    return {
      headline: `Ends on ${endDate}`,
      detail:
        'You have cancelled this subscription. It keeps working until then, and you will not be charged again.',
      tone: 'attention',
    };
  }

  switch (status) {
    case BillingSubscriptionStatus.Active:
      return {
        headline: 'Active',
        detail: `Renews on ${endDate}.`,
        tone: 'good',
      };

    case BillingSubscriptionStatus.Trialing:
      return {
        headline: 'Trial',
        detail: `Your trial runs until ${endDate}.`,
        tone: 'good',
      };

    case BillingSubscriptionStatus.PastDue:
      return {
        headline: 'Active — last payment failed',
        detail:
          'Stripe is retrying the payment and your access continues meanwhile. Update your card to keep it.',
        tone: 'attention',
      };

    case BillingSubscriptionStatus.Unpaid:
      return {
        headline: 'Suspended',
        detail:
          'The payment kept failing and Stripe has stopped retrying, so access has stopped. Settling the outstanding invoice restores it.',
        tone: 'ended',
      };

    case BillingSubscriptionStatus.Paused:
      return {
        headline: 'Paused',
        detail: 'Access has stopped while this subscription is paused.',
        tone: 'ended',
      };

    case BillingSubscriptionStatus.Incomplete:
      return {
        headline: 'Awaiting first payment',
        detail:
          'The first payment has not gone through, so this subscription is not active yet.',
        tone: 'ended',
      };

    case BillingSubscriptionStatus.IncompleteExpired:
      return {
        headline: 'Never started',
        detail:
          'The first payment was never completed, so this subscription expired before it began.',
        tone: 'ended',
      };

    case BillingSubscriptionStatus.Canceled:
    default:
      return {
        headline: 'Cancelled',
        detail: `This subscription has ended. Its last period ran to ${endDate}.`,
        tone: 'ended',
      };
  }
};
